import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Nav, NavItem, NavLink, Spinner } from 'reactstrap'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
/* Components */
import useGetSponsorObjects from '../query/hooks/useGetSponsorObjects'

const TAB_COUNT_THRESHOLD = 3
const WIDTH_PER_TAB = 160

const SponsorObjectTabs = ({onClickTab}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [useDropdown, setUseDropdown] = useState(false)
  const containerRef = useRef(null)
  const {data: sponsorObjects, isFetching} = useGetSponsorObjects();

  const { t } = useTranslation()

  const checkLayout = useCallback(() => {
    if (!containerRef.current || !sponsorObjects.length) return
    const totalTabs = sponsorObjects.length + 1 // +1 for Portfolio
    const containerWidth = containerRef.current.offsetWidth
    const fitsInTabs = totalTabs <= TAB_COUNT_THRESHOLD + 1 && containerWidth >= totalTabs * WIDTH_PER_TAB
    setUseDropdown(!fitsInTabs)
  }, [sponsorObjects])

  useEffect(() => {
    checkLayout()
    const observer = new ResizeObserver(checkLayout)
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [checkLayout])

  function toggleTab(tabIndex, sponsorObjectId) {
    if (activeTab !== tabIndex) {
      setActiveTab(tabIndex)
      onClickTab(tabIndex, sponsorObjectId)
    }
  }

  function renderTab(tab, index) {
    return (
      <NavItem key={index+1}>
        <NavLink
          href="#"
          className={classnames({
            active: activeTab === index+1
          })}
          onClick={() => {
            toggleTab(index+1, tab.id)
          }}
        >
          {tab.name}
        </NavLink>
      </NavItem>
    );
  };

  const dropdownOptions = [
    { value: 0, label: t('Portfolio'), sponsorObjectId: 0 },
    ...sponsorObjects.map((tab, index) => ({
      value: index + 1,
      label: tab.name,
      sponsorObjectId: tab.id
    }))
  ]

  const selectedOption = dropdownOptions.find(opt => opt.value === activeTab) || dropdownOptions[0]

  return (
    <div className="flex align-items-center" ref={containerRef}>
      {isFetching && (
        <div className="mr-4">
          <Spinner />
        </div>
      )}
      {sponsorObjects.length > 0 && (
        useDropdown ? (
          <div className="mb-4 mt-4 flex-fill" style={{ maxWidth: 300 }}>
            <Select
              value={selectedOption}
              onChange={(option) => toggleTab(option.value, option.sponsorObjectId)}
              options={dropdownOptions}
              isSearchable={sponsorObjects.length > 5}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'var(--background-secondary)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)',
                }),
                singleValue: (base) => ({
                  ...base,
                  color: 'var(--text-primary)',
                }),
                input: (base) => ({
                  ...base,
                  color: 'var(--text-primary)',
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: 'var(--background-secondary)',
                  zIndex: 10,
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused
                    ? 'var(--hover-color)'
                    : 'transparent',
                  color: 'var(--text-primary)',
                }),
                indicatorSeparator: (base) => ({
                  ...base,
                  backgroundColor: 'var(--border-color)',
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  color: 'var(--text-secondary)',
                }),
              }}
            />
          </div>
        ) : (
          <Nav tabs className="nav-tabs-custom mb-4 mt-4 flex-fill">
            <NavItem>
              <NavLink
                className={classnames({
                  active: activeTab === 0
                })}
                onClick={() => {
                  toggleTab(0, 0)
                }}
              >
                <span className="d-none d-sm-block">{t('Portfolio')}</span>
              </NavLink>
            </NavItem>
            {sponsorObjects.map((tab, index) => renderTab(tab, index))}
          </Nav>
        )
      )}
    </div>
  )
}


SponsorObjectTabs.propTypes = {
    onClickTab: PropTypes.func.isRequired
}

export default SponsorObjectTabs